import { keyframes } from '@emotion/core';
import styled from '@emotion/styled';
import React from 'react';

import { color, font } from 'src/shared/styles/variables';

import { ColorVariant } from './ColorVariant';

enum LabelSize {
    Small = 'small',
    Medium = 'medium',
}
const shrinkIn = keyframes`
    0% {
        transform: translate(13px,26px) scale(1.3);
    }
    100% {
        transform: translate(0,0) scale(1);
    }`;
const shrinkOut = keyframes`
    0% {
        transform: translate(-13px,-26px) scale(0.75);
    }
    100% {
        transform: translate(0,0) scale(1);
    }`;
interface LabelProps {
    variant: ColorVariant;
    size: LabelSize;
}
const Label = styled.label<LabelProps>(({ variant, size }) => {
    let labelColor = '';
    switch (variant) {
        case ColorVariant.Error:
            labelColor = color.wildWatermelon;
            break;
        case ColorVariant.Active:
            labelColor = color.cornflowerBlueDark;
            break;
        default:
            labelColor = color.blueBayoux;
    }
    return {
        color: labelColor,
        ...font.roman(size === LabelSize.Small ? font.size.small : font.size.medium),
    };
});
interface FloatingLabelProps {
    active: boolean;
    fixed: boolean;
    size: LabelSize;
}
const Container = styled.div({
    position: 'relative',
    zIndex: 9,
});
const LabelContainer = styled.div<FloatingLabelProps>(({ active, fixed, size }) => {
    return {
        position: 'absolute',
        left: '8px',
        top: size === LabelSize.Small ? '-8px' : '16px',
        backgroundColor: color.white,
        paddingLeft: '4px',
        paddingRight: '4px',
        animationName: fixed ? '' : active ? `${shrinkIn}` : `${shrinkOut}`,
        animationDuration: '200ms',
        pointerEvents: 'none',
    };
});

interface Props {
    id: string;
    label: string;
    variant: ColorVariant;
    active: boolean;
    filled: boolean;
    floatingLabelFixed: boolean;
}
export const OutlinedLabel: React.FC<Props> = ({
    id,
    filled,
    floatingLabelFixed,
    active,
    label,
    variant,
}) => {
    const size = filled || active ? LabelSize.Small : LabelSize.Medium;
    const fixed = floatingLabelFixed || filled;
    return (
        <Container id={id}>
            <LabelContainer active={active} fixed={fixed} size={size}>
                <Label size={size} variant={variant}>
                    {label}
                </Label>
            </LabelContainer>
        </Container>
    );
};
